import type { Transcript } from '../player/player.interface';

import { useCallback, useEffect, useState } from 'react';

export function useTranscriptSearch(
  transcript: Transcript | null | undefined,
  scrollToTranscriptItem: (index: number) => void
): {
  search: string | undefined;
  onSearch: (search: string) => void;
  searchResultIndex: number[] | undefined;
  cuesIndex: number[][] | undefined;
  currentSearchResult: number | undefined;
  setCurrentSearchResult: (index: number) => void;
} {
  const [search, setSearch] = useState<string | undefined>();
  const [currentSearchResult, setCurrentSearchResult] = useState<number | undefined>(undefined);
  const [searchResultIndex, setSearchResultIndex] = useState<number[] | undefined>(undefined);
  const [cuesIndex, setCuesIndex] = useState<number[][] | undefined>(undefined);

  /** Handles search logic. Creates two indexes to facilitate scrolling to and highlighting the search results */
  const onSearch = useCallback(
    (search: string) => {
      if (search && transcript?.cues.length) {
        const regex = new RegExp(`(${search})`, 'gi');

        /** Index that tells us which cue has the search result (cueIndex = index[searchResultIndex])
         * This is used to scroll to the current search result when they are selected in the search bar
         */
        const searchResultsIndex: number[] = [];
        /** Index that tells us which search results a cue has ([searchResultIndexes] = reverseIndex[cueIndex])
         * This is used to highlight the correct search result in the cue
         */
        const cuesIndex: number[][] = Array.from({ length: transcript.cues.length });

        for (let cueIdx = 0; cueIdx < transcript.cues.length; cueIdx++) {
          const cue = transcript.cues[cueIdx];
          const parts = cue.text.match(regex);

          const newMatchesFound = parts?.length || 0;

          cuesIndex[cueIdx] = Array.from({ length: newMatchesFound }, (_, i) => searchResultsIndex.length + i);
          searchResultsIndex.push(...Array.from({ length: newMatchesFound }, () => cueIdx));
        }

        setSearchResultIndex(searchResultsIndex);
        setCuesIndex(cuesIndex);
        setCurrentSearchResult(Math.min(1, searchResultsIndex.length));
        if (searchResultsIndex.length) {
          scrollToTranscriptItem(searchResultsIndex[0]);
        }
      } else {
        setSearchResultIndex(undefined);
        setCurrentSearchResult(undefined);
      }
      setSearch(search);
    },
    [scrollToTranscriptItem, transcript?.cues]
  );

  // Scroll to the item when current search result changes
  useEffect(() => {
    if (currentSearchResult !== undefined && searchResultIndex && search) {
      scrollToTranscriptItem(searchResultIndex[currentSearchResult - 1]);
    }
  }, [currentSearchResult, scrollToTranscriptItem, search, searchResultIndex]);

  // Re-run search whenever the transcript changes
  useEffect(() => {
    if (search && transcript?.cues?.length) {
      onSearch(search);
    }
  }, [onSearch, search, transcript?.cues]);

  return {
    search,
    onSearch,
    cuesIndex,
    searchResultIndex,
    currentSearchResult,
    setCurrentSearchResult,
  };
}
